import React, { useContext, useEffect, useState } from 'react'
import { useStaticQuery, graphql, navigate } from 'gatsby'
import { Location } from '@reach/router'
import Cookies from 'js-cookie'
import IframePopup from '../components/IframePopup'
import { AuthContext } from './context/AuthContext'
import { ONE_HOUR_COOKIE_EXPIRE, USER_EXPERIENCES } from './constants'

function useSendUserExperienceGtm({ allRoutes, pathname }) {
  useEffect(() => {
    const isInsight = pathname.startsWith('/insights/')
    const isWindowDefined = typeof window !== 'undefined'

    if (isWindowDefined && isInsight) {
      const onlyInsightsRoutes = [
        ...(allRoutes?.allContentstackInsightArticle?.nodes ?? []),
        ...(allRoutes?.allContentstackInsightWebinar?.nodes ?? []),
      ]

      const currentInsight = onlyInsightsRoutes.find(({ url }) =>
        pathname.includes(url)
      )

      const currentInsightExperience =
        currentInsight?.user_experience?.experience

      if (currentInsightExperience) {
        window.dataLayer.push({
          userExperience: currentInsightExperience,
        })
      }
    }
  }, [pathname])
}

const GatedContentHandler = ({ children, router }) => {
  const [openIframe, setIsOpenIframe] = useState(false)
  const allRoutes = useStaticQuery(graphql`
    query {
      allContentstackContactUsLoggedIn(
        filter: {
          user_experience: { experience: { eq: "institutional-only" } }
        }
      ) {
        nodes {
          url
          user_experience {
            ...UserExperience
          }
        }
      }
      allContentstackInsightArticle {
        nodes {
          url
          user_experience {
            ...UserExperience
          }
        }
      }
      allContentstackInsightWebinar {
        nodes {
          url
          user_experience {
            ...UserExperience
          }
        }
      }
      allContentstackLandingPage(
        filter: {
          user_experience: { experience: { eq: "institutional-only" } }
        }
      ) {
        nodes {
          url
          user_experience {
            ...UserExperience
          }
        }
      }
    }
  `)

  const authCtx = useContext(AuthContext)

  let { pathname } = router.location

  useSendUserExperienceGtm({ allRoutes, pathname })

  const privatePages = Object.keys(allRoutes)
    .flatMap((key) => {
      return allRoutes[key].nodes
    })
    .filter((route) => {
      return (
        route.user_experience.experience === USER_EXPERIENCES.INSTITUTIONAL_ONLY
      )
    })
    .map((route) => route.url)

  if (pathname.charAt(pathname.length - 1) === '/') {
    pathname = pathname.substr(0, pathname.length - 1)
  }

  const isPrivatePage = privatePages.indexOf(pathname) >= 0

  useEffect(() => {
    if (
      pathname === '/contact-us-logged-in' &&
      !JSON.parse(sessionStorage.getItem('USER_DATA'))
    ) {
      // special case where we go to not logged in version of page
      navigate('/contact-us/not-logged', { replace: true })
      return
    }
    if (pathname === '/contact-us/not-logged' && authCtx?.state?.isLoggedIn) {
      navigate('/contact-us', { replace: true })
    } else if (isPrivatePage) {
      fetch('/api/harbor-cookie')
        .then((res) => res.json())
        .then((res) => {
          const isSignupViewed = Cookies.get('signupViewed') === 'true'
          if (!res && !isSignupViewed) {
            Cookies.set('signupViewed', 'true', {
              expires: ONE_HOUR_COOKIE_EXPIRE,
            })
            window.dataLayer.push({
              signUpPopupViewed: 'User reached 10 articles viewed',
            })
            setIsOpenIframe(true)
          } else {
            authCtx.actions.tryLogin()
          }
        })
    }
  }, [pathname, setIsOpenIframe])

  const handleCloseIframe = () => {
    setIsOpenIframe(false)
  }

  return (
    <>
      {openIframe && (
        <IframePopup
          src="https://forms.harborcapital.com/f/signup"
          isOpen={openIframe}
          onClose={handleCloseIframe}
        />
      )}
      {children}
    </>
  )
}

const withLocation = ({ children }) => {
  return (
    <Location>
      {(location) => (
        <GatedContentHandler children={children} router={location} />
      )}
    </Location>
  )
}
export default React.memo(withLocation)
